import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';

import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ObjectReference } from '@totem/types/common';
import { OrganizationHierarchy } from '@totem/types/organization';
import { isNotNull } from '@totem/utilities/common';
import { ORGANIZATIONS_ENDPOINT } from '@totem/utilities/endpoints';

const styles = {
  label: {
    fontSize: '12px',
    fontWeight: 600,
  },
};

type Props = {
  showRegionSelect?: boolean;
  showBuildingSelect?: boolean;
  onSelected: (
    organization: ObjectReference | null,
    region: ObjectReference | null,
    building: ObjectReference | null,
  ) => void;
};

const OrgHierarchyContainer = ({
  onSelected,
  showRegionSelect = true,
  showBuildingSelect = true,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [orgData, setOrgData] = useState<OrganizationHierarchy[]>([]);
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrganizationHierarchy>(null);
  const [selectedRegion, setSelectedRegion] =
    useState<OrganizationHierarchy>(null);
  const [selectedBuilding, setSelectedBuilding] =
    useState<OrganizationHierarchy>(null);

  const toReference = (
    item: OrganizationHierarchy | null,
  ): ObjectReference | null =>
    isNotNull(item) ? { id: item.id, name: item.name } : null;

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const uri = `${ORGANIZATIONS_ENDPOINT}/hierarchy`;
      fetch(uri, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: OrganizationHierarchy[]) => {
          setOrgData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  useEffect(() => {
    onSelected(
      toReference(selectedOrganization),
      toReference(selectedRegion),
      toReference(selectedBuilding),
    );
  }, [onSelected, selectedOrganization, selectedRegion, selectedBuilding]);

  const organizationOptions = useMemo(
    () =>
      orgData.map((org) => ({
        value: org.id,
        label: org.name,
      })),
    [orgData],
  );

  const regionOptions = useMemo(() => {
    if (!isNotNull(selectedOrganization)) {
      return [];
    }
    return (selectedOrganization.children || []).map((region) => ({
      value: region.id,
      label: region.name,
    }));
  }, [selectedOrganization]);

  const buildingOptions = useMemo(() => {
    if (!isNotNull(selectedRegion)) {
      return [];
    }
    return (selectedRegion.children || []).map((building) => ({
      value: building.id,
      label: building.name,
    }));
  }, [selectedRegion]);

  const handleOrganizationChange = (organizationId?: string) => {
    if (!organizationId) {
      setSelectedOrganization(null);

      setSelectedRegion(null);

      setSelectedBuilding(null);
      return;
    }
    const nextOrganization =
      orgData.find((org) => org.id === organizationId) || null;
    setSelectedOrganization(nextOrganization);

    setSelectedRegion(null);

    setSelectedBuilding(null);
  };

  const handleRegionChange = (regionId?: string) => {
    if (!regionId) {
      setSelectedRegion(null);

      setSelectedBuilding(null);
      return;
    }
    const nextRegion =
      selectedOrganization?.children?.find(
        (region) => region.id === regionId,
      ) || null;
    setSelectedRegion(nextRegion);

    setSelectedBuilding(null);
  };

  const handleBuildingChange = (buildingId?: string) => {
    if (!buildingId) {
      setSelectedBuilding(null);
      return;
    }
    const nextBuilding =
      selectedRegion?.children?.find(
        (building) => building.id === buildingId,
      ) || null;
    setSelectedBuilding(nextBuilding);
  };

  return (
    <div>
      {isLoading ? (
        <Spin />
      ) : (
        <div>
          <div style={styles.label}>Organization:</div>
          <div>
            <Select
              placeholder="Select Organization"
              options={organizationOptions}
              value={selectedOrganization?.id}
              onChange={handleOrganizationChange}
              allowClear
              style={{ minWidth: '220px' }}
            />
          </div>
          {showRegionSelect && (
            <>
              <div style={styles.label}>Region:</div>
              <div>
                <Select
                  placeholder="Select Region"
                  options={regionOptions}
                  value={selectedRegion?.id}
                  onChange={handleRegionChange}
                  allowClear
                  disabled={!selectedOrganization}
                  style={{ minWidth: '220px' }}
                />
              </div>
            </>
          )}
          {showBuildingSelect && (
            <>
              <div style={styles.label}>Building:</div>
              <div>
                <Select
                  placeholder="Select Building"
                  options={buildingOptions}
                  value={selectedBuilding?.id}
                  onChange={handleBuildingChange}
                  allowClear
                  disabled={!selectedRegion}
                  style={{ minWidth: '220px' }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OrgHierarchyContainer;
